import React, { createContext, useContext, useState, useCallback, ReactNode, useEffect, useRef, useMemo } from "react";
import { Color } from "three";
import { usePageScrollProgress } from "@tennr/frontend/hooks/useScrollProgress";
import { MotionValue } from "framer-motion";
import { getLuminance } from "@tennr/frontend/utils/getLuminance";
import { rgbToHex } from "@tennr/frontend/utils/rgbToHex";

export type ColorStop = { color: string | number[]; progress: number };

interface BackgroundColorContextValue {
  currentColor: [number, number, number];
  colorStops: ColorStop[];
  scrollProgress: MotionValue<number>;
  updateCurrentColor: (color: [number, number, number]) => void;
  getContrastingColor: (color?: [number, number, number], options?: ContrastOptions) => string;
  forceDynamicHeadings?: boolean; // Global override to make all headings dynamic
  isTransitioning: boolean; // Indicates if background is currently transitioning
  transitionDuration: number; // Duration of transitions in seconds
  onTransitionStart?: () => void; // Callback when transition starts
  onTransitionEnd?: () => void; // Callback when transition ends
}

interface ContrastOptions {
  // Override colors for specific luminance ranges
  lightTextColor?: string;
  darkTextColor?: string;
  // Contrast algorithm: 'invert' | 'luminance' (default)
  algorithm?: "invert" | "luminance";
  // Strength of the contrast effect (0-1, default 1)
  strength?: number;
}

const BackgroundColorContext = createContext<BackgroundColorContextValue | undefined>(undefined);

export const useBackgroundColor = () => {
  const context = useContext(BackgroundColorContext);
  if (!context) {
    throw new Error("useBackgroundColor must be used within BackgroundColorProvider");
  }
  return context;
};

interface BackgroundColorProviderProps {
  children: ReactNode;
  colorStops: ColorStop[];
  isTransitioning?: boolean;
  transitionDuration?: number;
  defaultStrength?: number; // Global default strength for all headings
}

export const BackgroundColorProvider: React.FC<BackgroundColorProviderProps> = ({
  children,
  colorStops,
  isTransitioning = false,
  transitionDuration = 0.4,
  defaultStrength = 50, // Global default strength
}) => {
  const [currentColor, setCurrentColor] = useState<[number, number, number]>(() => {
    // Initialize with the first color stop
    if (colorStops.length > 0) {
      const firstStop = colorStops[0];
      if (typeof firstStop.color === "string") {
        const color = new Color(firstStop.color);
        return [color.r, color.g, color.b];
      }
      return firstStop.color as [number, number, number];
    }
    return [0.933, 0.875, 0.82];
  });
  const scrollProgress = usePageScrollProgress();

  // RAF and throttling refs
  const rafId = useRef<number>(0);
  const lastProgress = useRef(0);
  const lastUpdate = useRef(0);

  // Color calculation cache
  const colorCache = useRef(new Map<string, string>());
  const CACHE_SIZE = 100; // Max cache entries
  const THROTTLE_MS = 16; // ~60fps
  const PROGRESS_THRESHOLD = 0.005; // Only update if progress changes by > 0.5%

  // Interpolate between two hex colors - memoized
  const interpolateHexColors = useMemo(
    () => (color1: string, color2: string, progress: number) => {
      // Convert hex to RGB
      const hex2rgb = (hex: string) => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result
          ? {
              r: parseInt(result[1], 16) / 255,
              g: parseInt(result[2], 16) / 255,
              b: parseInt(result[3], 16) / 255,
            }
          : { r: 0, g: 0, b: 0 };
      };

      const c1 = hex2rgb(color1);
      const c2 = hex2rgb(color2);

      // Interpolate
      const r = c1.r + (c2.r - c1.r) * progress;
      const g = c1.g + (c2.g - c1.g) * progress;
      const b = c1.b + (c2.b - c1.b) * progress;

      return rgbToHex(r, g, b);
    },
    [rgbToHex]
  );

  // Get a contrasting color based on the background - with caching
  const getContrastingColor = useCallback(
    (color?: [number, number, number], options?: ContrastOptions) => {
      const [r, g, b] = color || currentColor;

      // Create cache key
      const cacheKey = `${r.toFixed(3)},${g.toFixed(3)},${b.toFixed(3)}-${JSON.stringify(options || {})}`;

      // Check cache
      if (colorCache.current.has(cacheKey)) {
        return colorCache.current.get(cacheKey)!;
      }

      // Clear cache if too large
      if (colorCache.current.size > CACHE_SIZE) {
        const keysToDelete = Array.from(colorCache.current.keys()).slice(0, CACHE_SIZE / 2);
        keysToDelete.forEach((key) => colorCache.current.delete(key));
      }

      const luminance = getLuminance(r, g, b);
      const algorithm = options?.algorithm || "luminance";

      let result: string;

      if (algorithm === "invert") {
        // Simple inversion
        result = rgbToHex(1 - r, 1 - g, 1 - b);
      } else {
        // Luminance-based interpolation (default)
        const darkColor = options?.darkTextColor || "#5f4037"; // Dark green
        const lightColor = options?.lightTextColor || "#EEDFD1"; // Light beige
        const strength = options?.strength ?? defaultStrength; // Use global default or override

        // Smoothly interpolate between dark and light based on luminance
        // Map luminance (0-1) to interpolation progress (0-1)
        // We want dark text when luminance is high, light text when luminance is low
        let progress = 1 - luminance; // Invert so high luminance = 0 (dark text)

        // Apply strength modifier - at strength 0, progress is always 0.5 (neutral)
        // at strength 1, use full progress range
        // Values above 1 create more intense contrast
        progress = 0.5 + (progress - 0.5) * strength;

        // Clamp progress to 0-1 range to prevent invalid colors
        progress = Math.max(0, Math.min(1, progress));

        // Apply easing for smoother transition
        const easedProgress = Math.pow(progress, 2); // Quadratic easing

        result = interpolateHexColors(darkColor, lightColor, easedProgress);
      }

      // Cache the result
      colorCache.current.set(cacheKey, result);
      return result;
    },
    [currentColor, interpolateHexColors, rgbToHex, defaultStrength]
  );

  // Update current color based on scroll progress - throttled with RAF
  useEffect(() => {
    const unsubscribe = scrollProgress.on("change", (progress) => {
      const now = Date.now();

      // Throttle by time and progress change threshold
      if (now - lastUpdate.current < THROTTLE_MS) return;
      if (Math.abs(progress - lastProgress.current) < PROGRESS_THRESHOLD) return;

      // Cancel any pending RAF
      if (rafId.current) {
        cancelAnimationFrame(rafId.current);
      }

      // Schedule update with RAF
      rafId.current = requestAnimationFrame(() => {
        if (isTransitioning) {
          // Threshold mode - find the active color stop (no interpolation)
          let targetStopIndex = 0;
          const threshold = 0.01; // Match the threshold in SmoothThresholdBackground

          for (let i = colorStops.length - 1; i >= 0; i--) {
            if (progress >= colorStops[i].progress - threshold) {
              targetStopIndex = i;
              break;
            }
          }

          const targetStop = colorStops[targetStopIndex];
          let colorArray: number[];
          if (typeof targetStop.color === "string") {
            const color = new Color(targetStop.color);
            colorArray = [color.r, color.g, color.b];
          } else {
            colorArray = targetStop.color;
          }

          setCurrentColor(colorArray as [number, number, number]);
        } else {
          // Gradient mode - interpolate between stops
          let lowerIdx = 0;
          for (let i = 0; i < colorStops.length - 1; i++) {
            if (progress >= colorStops[i].progress && progress <= colorStops[i + 1].progress) {
              lowerIdx = i;
              break;
            }
            if (progress > colorStops[colorStops.length - 1].progress) {
              lowerIdx = colorStops.length - 2;
            }
          }

          const stopA = colorStops[lowerIdx];
          const stopB = colorStops[lowerIdx + 1];
          const segmentProgress = (progress - stopA.progress) / (stopB.progress - stopA.progress);

          // Convert colors to arrays if needed
          let colorAArray: number[];
          if (typeof stopA.color === "string") {
            const colorA = new Color(stopA.color);
            colorAArray = [colorA.r, colorA.g, colorA.b];
          } else {
            colorAArray = stopA.color;
          }

          let colorBArray: number[];
          if (typeof stopB.color === "string") {
            const colorB = new Color(stopB.color);
            colorBArray = [colorB.r, colorB.g, colorB.b];
          } else {
            colorBArray = stopB.color;
          }

          // Interpolate between the two colors
          const interpolatedColor: [number, number, number] = [
            colorAArray[0] + (colorBArray[0] - colorAArray[0]) * segmentProgress,
            colorAArray[1] + (colorBArray[1] - colorAArray[1]) * segmentProgress,
            colorAArray[2] + (colorBArray[2] - colorAArray[2]) * segmentProgress,
          ];

          setCurrentColor(interpolatedColor);
        }

        lastProgress.current = progress;
        lastUpdate.current = now;
      });
    });

    // Cleanup
    return () => {
      unsubscribe();
      if (rafId.current) {
        cancelAnimationFrame(rafId.current);
      }
    };
  }, [scrollProgress, colorStops, isTransitioning]);

  const updateCurrentColor = useCallback((color: [number, number, number]) => {
    setCurrentColor(color);
  }, []);

  // Reset to first color when colorStops change (route change)
  useEffect(() => {
    if (colorStops.length > 0) {
      const firstStop = colorStops[0];
      let colorArray: number[];
      if (typeof firstStop.color === "string") {
        const color = new Color(firstStop.color);
        colorArray = [color.r, color.g, color.b];
      } else {
        colorArray = firstStop.color;
      }
      setCurrentColor(colorArray as [number, number, number]);
    }
  }, [colorStops]);

  const value: BackgroundColorContextValue = {
    currentColor,
    colorStops,
    scrollProgress,
    updateCurrentColor,
    getContrastingColor,
    forceDynamicHeadings: true, // Enable global dynamic override
    isTransitioning,
    transitionDuration,
  };

  return <BackgroundColorContext.Provider value={value}>{children}</BackgroundColorContext.Provider>;
};
